/**
 * Order Reference 2 is driven by the customer's "Use Order Reference 2" setting.
 * Both the create-order screens (Final Mile / Expedited and LTL) share the same
 * rules, so the resolution lives here to keep the two in sync.
 */
import { getApi } from "../../../apis/api";
import { ADD_CUSTOMER } from "../../../apis/end-points";

/** Minimal shape needed from a customer/sub-customer row to resolve Order Reference 2. */
export interface OrderReference2Source {
  isUsedOrderReference2?: boolean;
  usedOrderReference2?: string | null;
}

/** Order Reference 2 configuration keyed by customer id. */
export type OrderReference2ConfigMap = Record<string, OrderReference2Source>;

/**
 * Resolves which customer governs Order Reference 2.
 *
 * The order is always created *for* the lowest selected customer, so a selected
 * sub-customer wins over its parent — even when the sub-customer has the setting
 * turned off. Objects loaded from an existing order/quote do not carry the flag
 * (it is `undefined`); those are treated as "unknown" so callers leave the
 * already-loaded reference value untouched.
 *
 * @returns the governing customer, or `null` when neither carries the setting.
 */
export const resolveOrderReference2Source = (
  customer?: OrderReference2Source | null,
  subCustomer?: OrderReference2Source | null
): OrderReference2Source | null => {
  if (subCustomer?.isUsedOrderReference2 !== undefined) return subCustomer;
  if (customer?.isUsedOrderReference2 !== undefined) return customer;
  return null;
};

/**
 * Value to prefill into Order Reference 2 for the governing customer.
 * Empty when the setting is off so a previous customer's value never lingers.
 */
const getOrderReference2Prefill = (
  source: OrderReference2Source | null
): string =>
  source?.isUsedOrderReference2 && source?.usedOrderReference2
    ? source.usedOrderReference2
    : "";

/**
 * Order Reference 2 is locked (read-only) when the governing customer has the
 * setting enabled — the value is fixed by their configuration.
 */
export const isOrderReference2Locked = (
  source: OrderReference2Source | null
): boolean => !!source?.isUsedOrderReference2;

/**
 * Resolves the Order Reference 2 to display, taking a loaded record into account.
 *
 * `loadedValue` is the reference2 stored on a saved quote (or `null` when the form
 * was not populated from one). The customer's configured value always wins when the
 * setting is on: it is authoritative and may have changed since the quote was saved,
 * so a quote saved with "124" shows the customer's current "999". When the setting
 * is off, the saved value is kept rather than cleared.
 */
export const getOrderReference2Value = (
  source: OrderReference2Source | null,
  loadedValue: string | null
): string =>
  !isOrderReference2Locked(source) && loadedValue !== null
    ? loadedValue
    : getOrderReference2Prefill(source);

/** Order Reference 2 is always the first entry of the `moreReferences` array. */
export const ORDER_REFERENCE_2_INDEX = 0;

/** One `{ id: config }` entry, or nothing when the customer carries no setting. */
const toConfigEntry = (customer: any): OrderReference2ConfigMap =>
  customer?.id && customer?.isUsedOrderReference2 !== undefined
    ? {
        [customer.id]: {
          isUsedOrderReference2: customer.isUsedOrderReference2,
          usedOrderReference2: customer.usedOrderReference2,
        },
      }
    : {};

/**
 * Reads customers' *current* Order Reference 2 configuration — each one's and its
 * parent's — from the customer endpoint.
 *
 * A saved quote stores reference2 as it was when the quote was created, and the
 * customer may have changed the setting since, so quote loads must ask the API
 * rather than trust the quote payload. The customer dropdowns cannot serve this:
 * they only fetch once the user opens them, which never happens on a quote load.
 *
 * Accepts every id the caller might end up holding (customer and sub-customer);
 * blank and duplicate ids are ignored. The result is keyed by customer id so
 * callers look up whichever row they hold without caring about the hierarchy.
 * Customers that fail to load are simply absent, which leaves the quote's saved
 * value in place.
 */
export const fetchOrderReference2Config = async (
  ...customerIds: (string | undefined)[]
): Promise<OrderReference2ConfigMap> => {
  const ids = Array.from(new Set(customerIds.filter(Boolean) as string[]));
  if (!ids.length) return {};
  const responses = await Promise.all(
    ids.map((id) => getApi<any>(`${ADD_CUSTOMER}${id}`))
  );
  return responses.reduce<OrderReference2ConfigMap>((config, response) => {
    const customer = response?.success ? response?.data : null;
    return {
      ...config,
      ...toConfigEntry(customer?.parentCustomer),
      ...toConfigEntry(customer),
    };
  }, {});
};